/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import { amountFormatter, getFeeDescriptionByFeeCode } from '../../../../components/draft/fees/fees.helper';
import { uiSchemaProperties } from '../../../../shared/enums/uiSchemaProperties';
import { useFees } from '../../../../components/draft/fees/useFees';
import { CURRENCY_POSITION_TYPE } from '../../../../shared/enums/FeeCellType';

const useStyles = makeStyles(() => ({
  containerHidden: {
    display: 'none',
  },
  title: {
    fontSize: '1.55rem',
    fontWeight: 600,
    marginBottom: '2.6rem',
  },
  subTitle: {
    display: 'block',
    fontSize: '1rem',
    fontWeight: 600,
    borderBottom: '1px solid #000000',
    paddingBottom: '1rem',
    marginBottom: '1rem',
  },
  feeTable: {
    width: '100%',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #000000',
    marginBottom: '1rem',
    '& td': {
      display: 'inline-block',
      verticalAlign: 'top',
    },
    '& td:nth-child(1)': {
      width: '20%',
    },
    '& td:nth-child(2)': {
      width: '50%',
    },
    '& td:nth-child(3)': {
      textAlign: 'right',
      width: '30%',
      fontSize: '1rem',
      fontWeight: 600,
    },
  },
  totalContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    '& span': {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
  },
}));

const PaymentSummaryDrawerSection = ({ submission, uiSchema, visible, className }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { getFeeAdditionalConfig } = useFees();
  const _feesSelection = uiSchema?.fees?.feesSelection?.[uiSchemaProperties.UI_DATA]?.values;
  const containerStyle = clsx(className, { [classes.containerHidden]: !visible });
  const currency = submission?.fees?.feesSelection?.currencySymbol || '';
  const currencyPosition = submission?.fees?.feesSelection?.currencyPosition === CURRENCY_POSITION_TYPE?.AFTER;
  const totalPrice = amountFormatter().format(submission?.fees?.feesSelection?.total || 0);
  const formattedTotalPrice = currencyPosition ? `${totalPrice} ${currency}` : `${currency} ${totalPrice}`;

  return (
    <div className={containerStyle}>
      <span className={classes.subTitle}>{t('summary.see.all.selected.fees')}</span>
      <table className={classes.feeTable} aria-label="payment-summary-widget-table">
        <tbody>
          {submission?.fees?.feesSelection?.selectedFees?.map((data) => {
            const subtotalPrice = amountFormatter().format(data?.subtotal);
            const formattedSubtotalPrice = currencyPosition
              ? `${subtotalPrice} ${currency}`
              : `${currency} ${subtotalPrice}`;
            return (
              <tr key={data.code}>
                <td>{data?.code}</td>
                <td
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      t(getFeeAdditionalConfig(data?.code).descriptionKey) ||
                        getFeeDescriptionByFeeCode(_feesSelection, data?.code),
                    ),
                  }}
                />
                <td>{formattedSubtotalPrice}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className={classes.totalContainer}>
        <span>{t('fees.total')}</span>
        <span>{formattedTotalPrice}</span>
      </div>
    </div>
  );
};

export default PaymentSummaryDrawerSection;
